import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import type { PackageListFilter, PackageResponse } from '../../types/packages';
import ConfirmDialog from '../offers/ConfirmDialog';
import PackagesTable from './PackagesTable';
import PackagesEmptyState from './PackagesEmptyState';
import { RUNNING_STATUSES, isOfferingEligible, moveId, sortForTable, type PackageRowAction } from './packageUtils';
import { packageActionErrorMessage } from './packageErrors';
import { usePackagesT } from './packagesI18n';

const FILTERS: PackageListFilter[] = ['DRAFT', 'PENDING_APPROVAL', 'SCHEDULED', 'LIVE', 'PAUSED', 'ENDED', 'REJECTED', 'SUSPENDED', 'ARCHIVED'];
/** ≤ 10 running per garage (R6), so one page normally holds the whole running set the order applies to. */
const PAGE_SIZE = 50;

/** Spec 081b — Promotions → Packages: list, status filter, row actions, display order, empty state. */
export default function PackagesTab() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = usePackagesT();
  const [list, setList] = useState<PackageResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<PackageListFilter | ''>('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [confirm, setConfirm] = useState<{ pkg: PackageResponse; action: PackageRowAction } | null>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [eligibleCount, setEligibleCount] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.listMyPackages(token, filter || undefined, page, PAGE_SIZE);
      setList(sortForTable(res.content ?? []));
      setTotalPages(Math.max(res.totalPages ?? 1, 1));
    } catch {
      setError(t('packages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [token, filter, page, t]);

  useEffect(() => { load(); }, [load]);

  const firstRun = !loading && !error && !filter && page === 0 && list.length === 0;
  useEffect(() => {
    if (!firstRun || !token || eligibleCount != null) return;
    api.listMyOfferingsV2(token)
      .then((offerings) => setEligibleCount((offerings ?? []).filter(isOfferingEligible).length))
      .catch(() => { /* unknown: the CTA shows and the editor explains */ });
  }, [firstRun, token, eligibleCount]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(id);
  }, [toast]);

  const runAction = async () => {
    if (!confirm || !token) return;
    const { pkg, action } = confirm;
    setActing(true);
    setActionError(null);
    try {
      const fn = { pause: api.pausePackage, resume: api.resumePackage, end: api.endPackage, archive: api.archivePackage, withdraw: api.withdrawPackage }[action];
      await fn(pkg.id, token, pkg.version);
      setConfirm(null);
      setToast(t('packages.toast.updated'));
      await load();
    } catch (err) {
      setActionError(packageActionErrorMessage(err, t));
    } finally {
      setActing(false);
    }
  };

  const move = async (id: string, position: number) => {
    if (!token || reordering) return;
    const runningIds = list.filter((p) => RUNNING_STATUSES.includes(p.status)).map((p) => p.id);
    const next = moveId(runningIds, id, position);
    if (!next) return;
    const before = list;
    const byId = new Map(list.map((p) => [p.id, p]));
    // Optimistic, like ServicesPage: the row moves under the cursor, not after the round trip.
    setList(sortForTable(list.map((p) => (next.includes(p.id) ? { ...p, displayOrder: next.indexOf(p.id) + 1 } : p))));
    setReordering(true);
    setError(null);
    try {
      // The server wants every non-hidden package exactly once: the reordered running set first, then the rest
      // in their current order (only reachable with no filter on one page, so `list` is that whole set).
      const restIds = list.filter((p) => !RUNNING_STATUSES.includes(p.status)).map((p) => p.id);
      const saved = await api.reorderPackages([...next, ...restIds], token);
      (saved ?? []).forEach((p) => byId.set(p.id, p));
      setList(sortForTable(list.map((p) => byId.get(p.id) ?? p)));
    } catch {
      setList(before);
      setToast(t('packages.reorder.failed'));
    } finally {
      setReordering(false);
    }
  };

  const header = (
    <div className="partner-services-header">
      <div className="partner-services-header-left">
        <h2>{t('packages.title')}</h2>
        <p>{t('packages.subtitle')}</p>
      </div>
      <button type="button" className="partner-services-add-btn offer-new-btn" onClick={() => navigate('/promotions/packages/new')}>
        + {t('packages.new')}
      </button>
    </div>
  );

  if (loading && list.length === 0) return <>{header}<p className="offer-muted-center" role="status">{t('packages.loading')}</p></>;
  if (error) {
    return (
      <>
        {header}
        <div className="error-message" role="alert">{error}</div>
        <button type="button" className="offer-btn offer-btn--ghost" onClick={load}>{t('packages.retry')}</button>
      </>
    );
  }
  if (firstRun) {
    return (
      <PackagesEmptyState t={t} eligibleCount={eligibleCount}
        onCreate={() => navigate('/promotions/packages/new')} onGoToServices={() => navigate('/services')} />
    );
  }

  const reorderEnabled = !filter && totalPages <= 1;
  return (
    <>
      {header}
      <div className="offer-toolbar">
        <label htmlFor="pkg-status-filter" className="offer-toolbar__label">{t('packages.filter.label')}</label>
        <select id="pkg-status-filter" className="offer-select" value={filter}
          onChange={(e) => { setFilter(e.target.value as PackageListFilter | ''); setPage(0); }}>
          <option value="">{t('packages.filter.all')}</option>
          {FILTERS.map((f) => (
            <option key={f} value={f}>{f === 'ARCHIVED' ? t('packages.filter.ARCHIVED') : t(`packages.status.${f}`)}</option>
          ))}
        </select>
        {reorderEnabled && list.some((p) => RUNNING_STATUSES.includes(p.status)) && <span className="offer-muted">{t('packages.reorder.hint')}</span>}
      </div>

      {list.length === 0 ? <p className="offer-muted-center">{t('packages.filter.empty')}</p> : (
        <PackagesTable t={t} lang={lang} packages={list} archived={filter === 'ARCHIVED'} reorderEnabled={reorderEnabled}
          reordering={reordering} onOpen={(id) => navigate(`/promotions/packages/${id}`)} onMove={move}
          onAction={(pkg, action) => { setActionError(null); setConfirm({ pkg, action }); }} />
      )}

      {totalPages > 1 && (
        <div className="partner-pagination">
          <div className="partner-pagination-left" />
          <div className="partner-pagination-right">
            <span className="partner-pagination-info" dir="ltr">{page + 1} / {totalPages}</span>
            <button type="button" className="partner-pagination-btn" disabled={page <= 0} onClick={() => setPage(page - 1)} aria-label="Previous page">‹</button>
            <button type="button" className="partner-pagination-btn" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} aria-label="Next page">›</button>
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title={t(`packages.confirm.${confirm.action}.title`)} body={t(`packages.confirm.${confirm.action}.body`)}
          confirmLabel={t(`packages.action.${confirm.action}`)} cancelLabel={t('packages.confirm.cancel')}
          danger={confirm.action === 'end' || confirm.action === 'archive'} busy={acting} error={actionError}
          onConfirm={runAction} onCancel={() => setConfirm(null)}
        />
      )}
      {toast && <div className="partner-toast success" role="status">{toast}</div>}
    </>
  );
}
